import { all, delay, fork, put, select, takeEvery } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import get from 'lodash.get';
import set from 'lodash.set';

import getValidateFunction from 'forms/utils/get-validate-function';

import {
    VirtualizedTableItemValueSetAction,
    VirtualizedTableItemsValueSetAction
} from './types';
import { setItem, setError, removeError } from './actions';
import {
    selectVirtualizedTableItemsKeyedByKey,
    makeSelectVirtualizedTableItem,
    makeSelectVirtualizedTableColumnValidators
} from './selectors';
import {
    VIRTUALIZED_TABLE_ITEM_VALUE_SET,
    VIRTUALIZED_TABLE_ITEMS_VALUE_SET
} from './constants';

export function* setValueOnItem(
    action: VirtualizedTableItemValueSetAction
): SagaIterator {
    const {
        payload: { itemKey, itemField, itemValue, options }
    } = action;

    const ignoreValidation = options ? options.ignoreValidation : false;

    const columnValidators = yield select(
        makeSelectVirtualizedTableColumnValidators(),
        {
            columnKey: itemField
        }
    );

    let errorMessage;

    if (!ignoreValidation) {
        const validate = getValidateFunction(columnValidators);
        errorMessage = validate ? validate(itemValue) : undefined;
    }

    const item = yield select(makeSelectVirtualizedTableItem(), {
        itemKey
    });

    // Create a deep clone of the item
    const nextItem = JSON.parse(JSON.stringify(item));

    set(nextItem, itemField, itemValue);

    if (errorMessage) {
        yield put(setError(itemKey, itemField, errorMessage));
    } else {
        yield put(removeError(itemKey, itemField));

        // Prevent items from reordering before the error has been removed.
        yield delay(1);

        yield put(setItem([nextItem]));
    }
}

export function* watchItemValueSet(): SagaIterator {
    yield takeEvery(VIRTUALIZED_TABLE_ITEM_VALUE_SET, setValueOnItem);
}

export function* setValueOnItems(
    action: VirtualizedTableItemsValueSetAction
): SagaIterator {
    const {
        payload: { itemKeys, itemField, itemValue }
    } = action;

    const itemsKeyedByKey = yield select(selectVirtualizedTableItemsKeyedByKey);

    const nextItems = itemKeys.map(itemKey => {
        const item = get(itemsKeyedByKey, itemKey);

        // Create a deep clone of the item
        const nextItem = JSON.parse(JSON.stringify(item));

        set(nextItem, itemField, itemValue);

        return nextItem;
    });

    yield put(setItem(nextItems));
}

export function* watchItemsValueSet(): SagaIterator {
    yield takeEvery(VIRTUALIZED_TABLE_ITEMS_VALUE_SET, setValueOnItems);
}

export default function* virtualizedTableSagas(): SagaIterator {
    yield all([fork(watchItemValueSet), fork(watchItemsValueSet)]);
}
